import { Admin, Resource, CustomRoutes } from "react-admin";
import { Route } from "react-router-dom";
import simpleRestProvider from "ra-data-simple-rest";
import { tracedHttpClient } from "../api/TracedHttpClient";
import { authProvider } from "../auth/authProvider";
import { hasPermission, type Permission } from "../auth/HasPermission";
import { AppLayout } from "./AppLayout";
import { Login } from "../screens/Login/Login";
import Assignment from "@mui/icons-material/Assignment";
import Language from "@mui/icons-material/Language";
import Router from "@mui/icons-material/Router";
import BarChart from "@mui/icons-material/BarChart";
import { Dashboard } from "../screens/Dashboard/Dashboard";
import { TaskList } from "../screens/TaskList/TaskList";
import { TaskCreation } from "../screens/TaskCreation/TaskCreation";
import { TaskDetail } from "../screens/TaskDetail/TaskDetail";
import { SiteList } from "../screens/SiteList/SiteList";
import { SiteImport } from "../screens/SiteImport/SiteImport";
import { SiteVerifications } from "../screens/SiteVerifications/SiteVerifications";
import { ProxyList } from "../screens/ProxyList/ProxyList";
import { ProxyImport } from "../screens/ProxyImport/ProxyImport";
import { StatisticList } from "../screens/StatisticList/StatisticList";
import { StatisticDetail } from "../screens/StatisticDetail/StatisticDetail";
import { RoleList } from "../screens/RoleList/RoleList";
import { RoleEditor } from "../screens/RoleEditor/RoleEditor";
import { UserList } from "../screens/UserList/UserList";
import { UserEditor } from "../screens/UserEditor/UserEditor";
import { CompetitorLinkRemovalList } from "../screens/CompetitorLinkRemoval/CompetitorLinkRemovalList";
import { CompetitorLinkRemovalStart } from "../screens/CompetitorLinkRemoval/CompetitorLinkRemovalStart";
import { CompetitorLinkRemovalDetail } from "../screens/CompetitorLinkRemoval/CompetitorLinkRemovalDetail";
import AdminPanelSettings from "@mui/icons-material/AdminPanelSettings";
import People from "@mui/icons-material/People";
import LinkOff from "@mui/icons-material/LinkOff";
import { lightTheme, darkTheme } from "./theme";

const dataProvider = simpleRestProvider("/api", tracedHttpClient);

export function App() {
  return (
    <Admin
      dataProvider={dataProvider}
      authProvider={authProvider}
      loginPage={Login}
      dashboard={Dashboard}
      layout={AppLayout}
      theme={lightTheme}
      darkTheme={darkTheme}
    >
      {(permissions: Permission[]) => (
        <>
          {hasPermission(permissions, "tasks.view") && (
            <Resource
              name="tasks"
              list={TaskList}
              create={hasPermission(permissions, "tasks.create") ? TaskCreation : undefined}
              show={TaskDetail}
              icon={Assignment}
            />
          )}
          {hasPermission(permissions, "competitor-link-removals.view") && (
            <Resource
              name="competitor-link-removals"
              options={{ label: "Links remover" }}
              list={CompetitorLinkRemovalList}
              create={
                hasPermission(permissions, "competitor-link-removals.create")
                  ? CompetitorLinkRemovalStart
                  : undefined
              }
              show={CompetitorLinkRemovalDetail}
              icon={LinkOff}
            />
          )}
          {hasPermission(permissions, "sites.view") && (
            <Resource
              name="sites"
              list={SiteList}
              create={hasPermission(permissions, "sites.import") ? SiteImport : undefined}
              icon={Language}
            />
          )}
          {hasPermission(permissions, "proxies.view") && (
            <Resource
              name="proxies"
              list={ProxyList}
              create={hasPermission(permissions, "proxies.import") ? ProxyImport : undefined}
              icon={Router}
            />
          )}
          {hasPermission(permissions, "statistics.view") && (
            <Resource name="statistics" list={StatisticList} icon={BarChart} />
          )}
          {hasPermission(permissions, "roles.view") && (
            <Resource
              name="roles"
              list={RoleList}
              create={hasPermission(permissions, "roles.create") ? RoleEditor : undefined}
              edit={hasPermission(permissions, "roles.manage") ? RoleEditor : undefined}
              icon={AdminPanelSettings}
            />
          )}
          {hasPermission(permissions, "users.view") && (
            <Resource
              name="users"
              list={UserList}
              create={hasPermission(permissions, "users.create") ? UserEditor : undefined}
              edit={hasPermission(permissions, "users.manage") ? UserEditor : undefined}
              icon={People}
            />
          )}
          <CustomRoutes>
            {hasPermission(permissions, "statistics.view") ? (
              <Route path="/statistics/detail" element={<StatisticDetail />} />
            ) : null}
            {hasPermission(permissions, "sites.view") ? (
              <Route path="/sites/verifications" element={<SiteVerifications />} />
            ) : null}
          </CustomRoutes>
        </>
      )}
    </Admin>
  );
}
